<template>
  <div class="recommended-keywords-container p-4">
    <!-- Header -->
    <!-- <div class="flex justify-between items-center mb-6">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">
          Recommended Keywords
        </h2>
        <p class="text-gray-600 mt-1">
          Discover high-potential keywords based on competitor analysis and
          local search trends
        </p>
      </div>
      <div class="flex gap-2">
        <v-btn
          color="primary"
          outlined
          @click="refreshRecommendations"
          :loading="isRefreshing"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          color="primary"
          @click="addSelectedKeywords"
          :disabled="selectedKeywords.length === 0"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Selected ({{ selectedKeywords.length }})
        </v-btn>
      </div>
    </div> -->

    <!-- Location Selector -->
    <!-- <div class="mb-6" v-if="stores.length > 0">
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex items-center gap-4">
          <div class="flex-1">
            <v-btn @click="toggleStoreDropdown" outlined color="primary">
              {{ selectedWIDs?.length || 0 }} Selected Locations
            </v-btn>
            <LocationDropdown
              class="-mt-4"
              ref="locationDropdown"
              :stores="filteredStores"
              :showStoreDropdown="showStoreDropdown"
              :show-store-dropdown="showStoreDropdown"
              v-model="localSelectedWIDs"
              @change="handleLocationChange"
            />
          </div>
        </div>
      </div>
    </div> -->

    <!-- High-Level Overview Metrics -->
    <div
      class="mb-6"
      v-if="recommendations.length > 0 && !isLoading"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <!-- Total Recommendations -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <h3 class="text-sm font-medium text-gray-500 mb-2">
            Total Recommendations
          </h3>
          <div class="flex items-center justify-between">
            <p class="text-2xl font-bold text-primary">
              {{ formatNumber(recommendations.length) }}
            </p>
          </div>
        </div>

        <!-- Total Search Volume -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <h3 class="text-sm font-medium text-gray-500 mb-2">
            Total Search Volume
          </h3>
          <p class="text-2xl font-bold text-primary">
            {{ formatSearchVolume(getTotalSearchVolume()) }}
          </p>
        </div>

        <!-- Average Competition Index -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">
              Avg Competition Index
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span
                >Average competition index across all keywords.<br />Higher
                numbers = more competition (Scale: 0-100)</span
              >
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-orange-600">
            {{ getAverageCompetitionIndex() }}
          </p>
        </div>

        <!-- Lowest CPC -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">Lowest CPC</h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Lowest cost per click among all keywords</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-green-600">
            {{ formattedLowestCPC }}
          </p>
        </div>

        <!-- Highest CPC -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">Highest CPC</h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Highest cost per click among all keywords</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-red-500">
            {{ formattedHighestCPC }}
          </p>
        </div>

        <!-- Generated On -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <h3 class="text-sm font-medium text-gray-500 mb-2">Generated On</h3>
          <p class="text-lg font-semibold text-primary">
            {{ formatDate(keywordSummary?.created_at) }}
          </p>
        </div>
      </div>
    </div>

    <!-- Bid Range Overview -->
    <div
      class="mb-6"
      v-if="recommendations.length > 0 && !isLoading"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Lowest Low Top Bid -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">
              Lowest Low Top Bid
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Cheapest minimum bid to get ads at the top</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-blue-600">
            {{ formattedLowestLowTopBid }}
          </p>
        </div>

        <!-- Highest Low Top Bid -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">
              Highest Low Top Bid
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Most expensive minimum bid to get ads at the top</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-cyan-600">
            {{ formattedHighestLowTopBid }}
          </p>
        </div>

        <!-- Lowest High Top Bid -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">
              Lowest High Top Bid
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Cheapest maximum bid to get ads at the top</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-purple-600">
            {{ formattedLowestHighTopBid }}
          </p>
        </div>

        <!-- Highest High Top Bid -->
        <div
          class="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div class="flex items-center gap-1 mb-2">
            <h3 class="text-sm font-medium text-gray-500">
              Highest High Top Bid
            </h3>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on" color="gray">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Most expensive maximum bid to get ads at the top</span>
            </v-tooltip>
          </div>
          <p class="text-2xl font-bold text-red-600">
            {{ formattedHighestHighTopBid }}
          </p>
        </div>
      </div>
    </div>

    <!-- Competition Distribution Chart -->
    <div
      class="mb-6"
      v-if="recommendations.length > 0 && !isLoading"
    >
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-800">
            Competition Level Distribution
          </h3>
          <v-icon color="primary">mdi-chart-donut</v-icon>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Low Competition -->
          <div class="text-center p-4 bg-green-50 rounded-lg">
            <div class="text-2xl font-bold text-green-600 mb-1">
              {{ getCompetitionDistribution().low }}
            </div>
            <div class="text-sm text-green-700 font-medium mb-1">
              Low Competition
            </div>
            <div class="text-xs text-green-600">
              {{ getCompetitionPercentage("low") }}% of keywords
            </div>
          </div>

          <!-- Medium Competition -->
          <div class="text-center p-4 bg-yellow-50 rounded-lg">
            <div class="text-2xl font-bold text-yellow-600 mb-1">
              {{ getCompetitionDistribution().medium }}
            </div>
            <div class="text-sm text-yellow-700 font-medium mb-1">
              Medium Competition
            </div>
            <div class="text-xs text-yellow-600">
              {{ getCompetitionPercentage("medium") }}% of keywords
            </div>
          </div>

          <!-- High Competition -->
          <div class="text-center p-4 bg-red-50 rounded-lg">
            <div class="text-2xl font-bold text-red-600 mb-1">
              {{ getCompetitionDistribution().high }}
            </div>
            <div class="text-sm text-red-700 font-medium mb-1">
              High Competition
            </div>
            <div class="text-xs text-red-600">
              {{ getCompetitionPercentage("high") }}% of keywords
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center h-64">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="40"
        ></v-progress-circular>
        <p class="mt-4 text-gray-600">Loading recommended keywords...</p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-white rounded-lg shadow-sm p-8 text-center"
    >
      <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
      <h3 class="text-lg font-medium text-gray-900 mt-4">Error Loading Data</h3>
      <p class="text-gray-600 mt-2">{{ error }}</p>
      <v-btn color="primary" outlined class="mt-4" @click="loadRecommendations">
        Try Again
      </v-btn>
    </div>

    <!-- Recommendations Content -->
    <div v-else-if="recommendations.length > 0">
      <!-- Filter and Sort Controls -->
      <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div class="flex flex-wrap gap-4 items-center">
          <div class="flex-1 min-w-64">
            <v-text-field
              v-model="searchQuery"
              label="Search keywords..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              clearable
            ></v-text-field>
          </div>
          <div class="min-w-48">
            <v-select
              v-model="competitionFilter"
              :items="competitionOptions"
              label="Competition Level"
              outlined
              dense
              clearable
            ></v-select>
          </div>
          <div class="min-w-48">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              label="Sort by"
              outlined
              dense
              clearable
              @input="handleSortChange"
            ></v-select>
          </div>
        </div>
      </div>

      <!-- Recommendations Table -->
      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4 border-b">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-medium text-gray-800">
              Keyword Recommendations
              <span class="text-sm text-gray-500 ml-2">
                ({{ filteredRecommendations.length }} keywords)
              </span>
            </h3>
            <v-btn
              color="primary"
              :disabled="selectedKeywords.length === 0"
              @click="addSelectedKeywords"
            >
              <v-icon left>mdi-plus</v-icon>
              Add To Rank Tracker ({{ selectedKeywords.length }})
            </v-btn>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left">
                  <v-checkbox
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    hide-details
                  ></v-checkbox>
                </th>
                <th
                  class="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('keyword')"
                >
                  <div class="flex items-center gap-1">
                    Keyword
                    <v-icon small color="gray">
                      {{ getSortIcon("keyword") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('searchVolume')"
                >
                  <div class="flex items-center justify-center gap-1">
                    Search Volume
                    <v-icon small color="gray">
                      {{ getSortIcon("searchVolume") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700"
                >
                  <div class="flex items-center justify-center gap-1">
                    Monthly Trend
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>Monthly search volume trend over time</span>
                    </v-tooltip>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('competition')"
                >
                  <div class="flex items-center justify-center gap-1">
                    Competition
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span
                        >Shows how many advertisers are bidding on this
                        keyword.</span
                      >
                    </v-tooltip>
                    <v-icon small color="gray">
                      {{ getSortIcon("competition") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('competitionIndex')"
                >
                  <div class="flex items-center justify-center gap-1">
                    Competition Index
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span
                        >Higher numbers = more advertisers competing for this
                        keyword.</span
                      >
                    </v-tooltip>
                    <v-icon small color="gray">
                      {{ getSortIcon("competitionIndex") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('cpc')"
                >
                  <div class="flex items-center justify-center gap-1">
                    CPC
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span
                        >Amount advertisers pay when someone clicks their
                        ad.</span
                      >
                    </v-tooltip>
                    <v-icon small color="gray">
                      {{ getSortIcon("cpc") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('lowBid')"
                >
                  <div class="flex items-center justify-center gap-1">
                    Low Top Bid
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>Cheapest price to get your ad at the top</span>
                    </v-tooltip>
                    <v-icon small color="gray">
                      {{ getSortIcon("lowBid") }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                  @click="toggleSort('highBid')"
                >
                  <div class="flex items-center justify-center gap-1">
                    High Top Bid
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on" color="gray">
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span
                        >Most expensive price to get your ad at the top</span
                      >
                    </v-tooltip>
                    <v-icon small color="gray">
                      {{ getSortIcon("highBid") }}
                    </v-icon>
                  </div>
                </th>
                <!-- <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700"
                >
                  Opportunity Score
                </th> -->
                <th
                  class="px-4 py-3 text-center text-sm font-medium text-gray-700"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="keyword in paginatedRecommendations"
                :key="keyword.keyword"
                class="border-b hover:bg-gray-50 transition-colors"
              >
                <td class="px-4 py-3">
                  <v-checkbox
                    v-model="selectedKeywords"
                    :value="keyword.keyword"
                    hide-details
                  ></v-checkbox>
                </td>
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-900">
                    {{ keyword.keyword }}
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <span class="text-sm font-medium">
                    {{ formatSearchVolume(keyword.search_volume) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <div class="flex items-center justify-center">
                    <div class="w-20 h-8 relative">
                      <v-menu
                        v-model="trendTooltipMenus[keyword.keyword]"
                        :close-on-content-click="false"
                        :close-on-click="false"
                        offset-y
                        bottom
                        :nudge-top="-10"
                        max-width="300"
                        :open-on-hover="true"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <canvas
                            :ref="`monthlyTrend_${keyword.keyword.replace(
                              /[^a-zA-Z0-9]/g,
                              '_'
                            )}`"
                            width="80"
                            height="32"
                            class="w-full h-full cursor-pointer"
                            v-bind="attrs"
                            v-on="on"
                          ></canvas>
                        </template>
                        <v-card class="pa-3 bg-gray-800 text-white" dark>
                          <div class="text-xs font-medium mb-2 text-white">
                            {{ keyword.keyword }}
                          </div>
                          <div v-html="getTrendTooltipContent(keyword)"></div>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3 text-center">
                  <span
                    class="text-sm font-medium"
                    :class="getCompetitionTextColor(keyword.competition)"
                  >
                    {{ getCompetitionLabel(keyword.competition) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <span class="text-sm font-medium">
                    {{ keyword.competition_index || "N/A" }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <CurrencyDisplay
                    class="text-sm font-medium"
                    :amount="keyword.cpc"
                    :countryCode="userCountryCode"
                  />
                </td>
                <td class="px-4 py-3 text-center">
                  <CurrencyDisplay
                    class="text-sm"
                    :amount="keyword.low_top_of_page_bid"
                    :countryCode="userCountryCode"
                  />
                </td>
                <td class="px-4 py-3 text-center">
                  <CurrencyDisplay
                    class="text-sm"
                    :amount="keyword.high_top_of_page_bid"
                    :countryCode="userCountryCode"
                  />
                </td>
                <!-- <td class="px-4 py-3 text-center">
                  <div class="flex items-center justify-center">
                    <div
                      class="w-16 h-2 rounded-full mr-2"
                      :class="getOpportunityColor(keyword.opportunityScore)"
                    ></div>
                    <span class="text-sm font-medium">
                      {{ keyword.opportunityScore }}/100
                    </span>
                  </div>
                </td> -->
                <td class="px-4 py-3 text-center">
                  <v-btn
                    small
                    color="primary"
                    @click="addSingleKeyword(keyword.keyword)"
                  >
                    <v-icon small left>mdi-plus</v-icon>
                    Add To Rank Tracker
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          class="px-4 py-3 border-t bg-gray-50"
          v-if="filteredRecommendations.length > itemsPerPage"
        >
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="7"
            class="justify-center"
          ></v-pagination>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div
      v-else-if="recommendations.length === 0 && !isLoading"
    >
      <div class="bg-white rounded-lg shadow-sm p-8 text-center">
        <v-icon size="64" color="gray">mdi-lightbulb-outline</v-icon>
        <h3 class="text-lg font-medium text-gray-900 mt-4">
          No Keywords Found
        </h3>
        <p class="text-gray-600 mt-2">
          No recommended keywords are available. Try
          refreshing or check back later.
        </p>
      </div>
    </div>


  </div>
</template>

<script>
import CurrencyDisplay from "@/components/CurrencyDisplay.vue";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "@/utils/firebase.utils";
import Chart from "chart.js/auto";
import moment from "moment";
import {
  formatCurrency,
  formatCurrencyWithConversion,
} from "@/utils/currency.utils";

export default {
  name: "GmbRecommendedKeywords",
  components: {
    CurrencyDisplay,
  },
  props: {
    // Remove selectedWIDs dependency - component will work independently
  },
  data() {
    return {
      isLoading: false,
      isRefreshing: false,
      error: null,
      searchQuery: "",
      competitionFilter: null,
      sortBy: null,
      selectedKeywords: [],
      selectAll: false,
      currentPage: 1,
      itemsPerPage: 20,

      // Data from Firestore
      keywordSummary: null,
      recommendations: [],

      // Currency conversion cache and state
      currencyConversionCache: new Map(),
      isConvertingCurrency: false,
      convertedPrices: {
        lowestCPC: null,
        highestCPC: null,
        lowestLowTopBid: null,
        highestLowTopBid: null,
        lowestHighTopBid: null,
        highestHighTopBid: null,
      },

      // Sorting state
      currentSortField: "searchVolume",
      sortDirection: "desc",

      // Chart related data
      monthlyTrendCharts: {},
      trendTooltip: {
        show: false,
        x: 0,
        y: 0,
        content: "",
        keyword: null,
      },
      trendTooltipMenus: {},

      competitionOptions: [
        { text: "Low", value: "low" },
        { text: "Medium", value: "medium" },
        { text: "High", value: "high" },
      ],

      sortOptions: [
        { text: "Keyword (A to Z)", value: "keyword" },
        { text: "Keyword (Z to A)", value: "keywordDesc" },
        { text: "Search Volume (High to Low)", value: "searchVolume" },
        { text: "Search Volume (Low to High)", value: "searchVolumeAsc" },
        { text: "Competition (Low to High)", value: "competition" },
        { text: "Competition (High to Low)", value: "competitionDesc" },
        { text: "Competition Index (Low to High)", value: "competitionIndex" },
        {
          text: "Competition Index (High to Low)",
          value: "competitionIndexDesc",
        },
        { text: "CPC (Low to High)", value: "cpc" },
        { text: "CPC (High to Low)", value: "cpcDesc" },
        { text: "Low Top Bid (Low to High)", value: "lowBid" },
        { text: "Low Top Bid (High to Low)", value: "lowBidDesc" },
        { text: "High Top Bid (Low to High)", value: "highBid" },
        { text: "High Top Bid (High to Low)", value: "highBidDesc" },
      ],
    };
  },
  computed: {
    currentUID() {
      return this.$store.getters.currentUID;
    },

    WID() {
      return this.$store.getters.WID;
    },

    currentUser() {
      return this.$store.getters.currentUser;
    },

    authUser() {
      return this.$store.getters.authUser || this.currentUser || {};
    },

    userCountryCode() {
      return this.authUser.country_code || "US";
    },

    // Formatted currency values for template
    formattedLowestCPC() {
      return this.convertedPrices.lowestCPC || this.getLoadingText();
    },

    formattedHighestCPC() {
      return this.convertedPrices.highestCPC || this.getLoadingText();
    },

    formattedLowestLowTopBid() {
      return this.convertedPrices.lowestLowTopBid || this.getLoadingText();
    },

    formattedHighestLowTopBid() {
      return this.convertedPrices.highestLowTopBid || this.getLoadingText();
    },

    formattedLowestHighTopBid() {
      return this.convertedPrices.lowestHighTopBid || this.getLoadingText();
    },

    formattedHighestHighTopBid() {
      return this.convertedPrices.highestHighTopBid || this.getLoadingText();
    },


    keywordsWithVolumeData() {
      return this.recommendations.filter(
        (k) => k.search_volume && k.search_volume > 0
      ).length;
    },

    filteredRecommendations() {
      let filtered = [...this.recommendations];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((item) =>
          item.keyword.toLowerCase().includes(query)
        );
      }

      // Apply competition filter
      if (this.competitionFilter) {
        filtered = filtered.filter((item) => {
          const competition = item.competition || "";
          console.log("comp:", competition);
          switch (this.competitionFilter) {
            case "low":
              return competition === "LOW";
            case "medium":
              return competition === "MEDIUM";
            case "high":
              return competition === "HIGH";
            default:
              return true;
          }
        });
      }

      // Apply sorting (default to searchVolume if null)
      const activeSortBy = this.sortBy || "searchVolume";
      filtered.sort((a, b) => {
        switch (activeSortBy) {
          case "keyword":
            return a.keyword.localeCompare(b.keyword);
          case "keywordDesc":
            return b.keyword.localeCompare(a.keyword);
          case "searchVolume":
            return (b.search_volume || 0) - (a.search_volume || 0);
          case "searchVolumeAsc":
            return (a.search_volume || 0) - (b.search_volume || 0);
          case "competition":
            return (
              this.getCompetitionSortValue(a.competition) -
              this.getCompetitionSortValue(b.competition)
            );
          case "competitionDesc":
            return (
              this.getCompetitionSortValue(b.competition) -
              this.getCompetitionSortValue(a.competition)
            );
          case "competitionIndex":
            return (a.competition_index || 0) - (b.competition_index || 0);
          case "competitionIndexDesc":
            return (b.competition_index || 0) - (a.competition_index || 0);
          case "cpc":
            return (a.cpc || 0) - (b.cpc || 0);
          case "cpcDesc":
            return (b.cpc || 0) - (a.cpc || 0);
          case "lowBid":
            return (a.low_top_of_page_bid || 0) - (b.low_top_of_page_bid || 0);
          case "lowBidDesc":
            return (b.low_top_of_page_bid || 0) - (a.low_top_of_page_bid || 0);
          case "highBid":
            return (
              (a.high_top_of_page_bid || 0) - (b.high_top_of_page_bid || 0)
            );
          case "highBidDesc":
            return (
              (b.high_top_of_page_bid || 0) - (a.high_top_of_page_bid || 0)
            );
          default:
            return 0;
        }
      });

      return filtered;
    },

    paginatedRecommendations() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredRecommendations.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredRecommendations.length / this.itemsPerPage);
    },
  },
  watch: {

    selectedKeywords(newVal) {
      this.selectAll =
        newVal.length === this.filteredRecommendations.length &&
        this.filteredRecommendations.length > 0;
    },

    currentPage() {
      // Reinitialize charts when page changes
      this.$nextTick(() => {
        setTimeout(() => {
          this.initializeMonthlyTrendCharts();
        }, 200);
      });
    },

    filteredRecommendations() {
      // Reinitialize charts when filtering changes
      this.$nextTick(() => {
        setTimeout(() => {
          this.initializeMonthlyTrendCharts();
        }, 200);
      });
    },

    recommendations: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          // Initialize charts when recommendations data is loaded
          this.$nextTick(() => {
            setTimeout(() => {
              this.initializeMonthlyTrendCharts();
            }, 200);
          });

          // Convert currency values
          this.convertOverviewPrices();
        }
      },
      deep: true,
    },

    userCountryCode: {
      handler() {
        // Re-convert prices when country changes
        this.currencyConversionCache.clear();
        this.convertOverviewPrices();
      },
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    
    // Load recommendations automatically when component is mounted
    this.loadRecommendations();

    // Initialize charts when component is mounted and data becomes available
    this.$nextTick(() => {
      if (this.recommendations.length > 0) {
        this.initializeMonthlyTrendCharts();
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);

    // Clean up all charts
    Object.values(this.monthlyTrendCharts).forEach((chart) => {
      if (chart) chart.destroy();
    });
    this.monthlyTrendCharts = {};
  },
  methods: {

    handleSortChange(value) {
      this.sortBy = value;
    },

    handleClickOutside() {
      // Remove location dropdown handling since it's no longer needed
    },

    async loadRecommendations() {
      if (!this.currentUID) return;

      this.isLoading = true;
      this.error = null;

      try {
        await this.fetchKeywordRecommendations();
      } catch (error) {
        console.error("Error loading keyword recommendations:", error);
        this.error =
          "Failed to load keyword recommendations. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    async fetchKeywordRecommendations() {
      try {
        // Get the most recent keywords document
        const keywordsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "recommended_keywords"
        );

        // Query for the most recent document
        const recentQuery = query(
          keywordsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );

        const querySnapshot = await getDocs(recentQuery);

        if (querySnapshot.empty) {
          console.log("No keyword recommendations found");
          this.recommendations = [];
          this.keywordSummary = null;
          return;
        }

        const docData = querySnapshot.docs[0].data();
        this.keywordSummary = {
          total_keywords:
            docData.keywords_volume_data?.keywords?.length || "N/A",
          created_at: docData.created_at.seconds
            ? new Date(docData.created_at.seconds * 1000)
            : docData.created_at,
          updated_at: docData.updated_at.seconds
            ? new Date(docData.updated_at.seconds * 1000)
            : docData.updated_at,
        };

        // Process keywords data
        this.processKeywordsData(docData);
      } catch (error) {
        console.error("Error fetching keyword recommendations:", error);
        throw error;
      }
    },

    processMonthlySearches(monthlySearches) {
      if (!Array.isArray(monthlySearches) || monthlySearches.length === 0) {
        return [];
      }

      // Sort by year and month (oldest first)
      const sortedSearches = monthlySearches.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      // Convert to the expected format
      return sortedSearches.map((item) => ({
        month: this.formatMonthYear(item.year, item.month),
        search_volume: item.search_volume || 0,
      }));
    },

    formatMonthYear(year, month) {
      const date = new Date(year, month - 1, 1); // month is 1-based in data, 0-based in Date
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    },

    processKeywordsData(docData) {
      const keywordsVolumeData = docData.keywords_volume_data?.keywords || [];
      const allKeywords = docData.keywords || [];

      // Create recommendations array
      this.recommendations = [];

      // Process keywords with volume data
      keywordsVolumeData.forEach((keywordObj) => {
        const recommendation = {
          keyword: keywordObj.keyword,
          search_volume: keywordObj.search_volume || 0,
          competition: keywordObj.competition || 0,
          competition_index: keywordObj.competition_index || 0,
          cpc: keywordObj.cpc || 0,
          low_top_of_page_bid: keywordObj.low_top_of_page_bid || 0,
          high_top_of_page_bid: keywordObj.high_top_of_page_bid || 0,
          average_monthly_searches: keywordObj.average_monthly_searches || 0,
          monthly_search_data: this.processMonthlySearches(
            keywordObj.monthly_searches || []
          ),
          opportunityScore: this.calculateOpportunityScore(keywordObj),
        };
        this.recommendations.push(recommendation);
      });
      console.log("recommendations:", this.recommendations);

      // Add keywords without volume data
      allKeywords.forEach((keyword) => {
        const existingKeyword = this.recommendations.find(
          (r) => r.keyword === keyword
        );
        if (!existingKeyword) {
          this.recommendations.push({
            keyword: keyword,
            search_volume: 0,
            competition: 0,
            competition_index: 0,
            cpc: 0,
            low_top_of_page_bid: 0,
            high_top_of_page_bid: 0,
            average_monthly_searches: 0,
            monthly_search_data: [],
            opportunityScore: 50, // Default score for keywords without data
          });
        }
      });

      // Initialize charts after data is processed
      this.$nextTick(() => {
        this.initializeMonthlyTrendCharts();
      });
    },

    initializeMonthlyTrendCharts() {
      // Clean up existing charts
      Object.values(this.monthlyTrendCharts).forEach((chart) => {
        if (chart) chart.destroy();
      });
      this.monthlyTrendCharts = {};

      // Initialize overview trend chart
      this.initializeOverviewTrendChart();

      // Wait for DOM update before initializing charts
      setTimeout(() => {
        this.paginatedRecommendations.forEach((keyword) => {
          this.initializeKeywordTrendChart(keyword);
        });
      }, 100);
    },

    initializeOverviewTrendChart() {
      const canvas = this.$refs.totalRecommendationsTrend;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Generate trend data for total recommendations (mock data showing growth)
      const months = [];
      const currentDate = new Date();
      const data = [];

      for (let i = 11; i >= 0; i--) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const monthName = date.toLocaleDateString("en-US", { month: "short" });
        months.push(monthName);

        // Generate upward trending data
        const baseValue = this.recommendations.length || 50;
        const trendValue = Math.max(10, baseValue - i * 3 + Math.random() * 10);
        data.push(Math.round(trendValue));
      }

      // Clean up existing chart
      if (this.monthlyTrendCharts.overviewTrend) {
        this.monthlyTrendCharts.overviewTrend.destroy();
      }

      try {
        canvas.width = 80;
        canvas.height = 32;

        this.monthlyTrendCharts.overviewTrend = new Chart(ctx, {
          type: "line",
          data: {
            labels: months,
            datasets: [
              {
                data,
                borderColor: "#8B5CF6",
                backgroundColor: "#8B5CF620",
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { enabled: false },
            },
            scales: {
              x: { display: false },
              y: { display: false, beginAtZero: true },
            },
            animation: false,
          },
        });
      } catch (error) {
        console.error("Error creating overview trend chart:", error);
      }
    },

    initializeKeywordTrendChart(keyword) {
      if (!keyword || !keyword.keyword) {
        console.warn(
          "Invalid keyword data provided to initializeKeywordTrendChart"
        );
        return;
      }

      const chartId = `monthlyTrend_${keyword.keyword.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}`;
      const canvas = this.$refs[chartId]?.[0];

      if (!canvas) {
        console.warn(
          `Monthly trend canvas ${chartId} not found for keyword: ${keyword.keyword}`
        );
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.warn(`Monthly trend canvas context ${chartId} not ready`);
        return;
      }

      // Use real monthly data if available, otherwise generate mock data
      let monthlyData = [];

      if (
        keyword.monthly_search_data &&
        Array.isArray(keyword.monthly_search_data) &&
        keyword.monthly_search_data.length > 0
      ) {
        // Validate that the monthly data has proper structure
        const validData = keyword.monthly_search_data.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            item.search_volume !== undefined &&
            item.month !== undefined
        );

        if (validData.length > 0) {
          monthlyData = validData;
          console.log(
            `Using real monthly data for keyword: ${keyword.keyword}`,
            validData
          );
        } else {
          monthlyData = this.generateMockMonthlyData(
            keyword.search_volume || 0,
            keyword.keyword
          );
          console.log(
            `Using mock data for keyword: ${keyword.keyword} (invalid real data)`
          );
        }
      } else {
        monthlyData = this.generateMockMonthlyData(
          keyword.search_volume || 0,
          keyword.keyword
        );
        console.log(
          `Using mock data for keyword: ${keyword.keyword} (no real data)`
        );
      }

      // Ensure data arrays are properly formatted
      const data = monthlyData.map((item) => {
        const volume =
          item && typeof item === "object" ? item.search_volume || 0 : 0;
        return Math.max(0, Number(volume) || 0); // Ensure positive numbers
      });

      const labels = monthlyData.map((item, index) => {
        if (item && typeof item === "object" && item.month) {
          return String(item.month);
        }
        // Fallback to month names if no proper labels
        const date = new Date();
        date.setMonth(date.getMonth() - (monthlyData.length - 1 - index));
        return date.toLocaleDateString("en-US", { month: "short" });
      });

      // Calculate trend color based on actual data or pattern type
      let trendColor = "#6B7280"; // Default gray

      if (data.length >= 2) {
        // Check if we have real monthly data by looking at the data structure
        const hasRealData =
          keyword.monthly_search_data &&
          Array.isArray(keyword.monthly_search_data) &&
          keyword.monthly_search_data.length > 0 &&
          keyword.monthly_search_data.some(
            (item) =>
              item &&
              typeof item === "object" &&
              item.search_volume !== undefined &&
              item.month !== undefined
          );

        if (hasRealData) {
          // Use actual trend analysis for real data
          const firstValue = Number(data[0]) || 0;
          const lastValue = Number(data[data.length - 1]) || 0;
          const threshold = Math.max(
            firstValue * 0.15,
            Math.max(...data) * 0.1
          ); // 15% change or 10% of max

          if (lastValue > firstValue + threshold) {
            trendColor = "#10B981"; // Green for growth
          } else if (lastValue < firstValue - threshold) {
            trendColor = "#EF4444"; // Red for decline
          } else {
            // Check for volatility in real data
            const maxValue = Math.max(...data);
            const minValue = Math.min(...data);
            const volatility =
              (maxValue - minValue) / ((maxValue + minValue) / 2);

            if (volatility > 0.4) {
              trendColor = "#F59E0B"; // Orange for volatile
            } else {
              trendColor = "#14B8A6"; // Teal for stable
            }
          }
        } else {
          // Use pattern-based coloring for mock data
          let keywordSeed = keyword.search_volume || 1000;
          if (keyword.keyword) {
            for (let i = 0; i < keyword.keyword.length; i++) {
              keywordSeed += keyword.keyword.charCodeAt(i) * (i + 1);
            }
          }
          const patternType = keywordSeed % 5;

          switch (patternType) {
            case 0: // Declining trend - always red
              trendColor = "#EF4444";
              break;
            case 1: // Growing trend - always green
              trendColor = "#10B981";
              break;
            case 2: // U-shaped - purple/blue
              trendColor = "#8B5CF6";
              break;
            case 3: // Volatile - orange
              trendColor = "#F59E0B";
              break;
            case 4: // Inverted U - teal
            default:
              trendColor = "#14B8A6";
              break;
          }
        }
      }

      // Clean up existing chart
      if (this.monthlyTrendCharts[chartId]) {
        this.monthlyTrendCharts[chartId].destroy();
        delete this.monthlyTrendCharts[chartId];
      }

      try {
        // Validate data before creating chart
        if (!Array.isArray(data) || data.length === 0) {
          console.warn(
            `No valid data for chart ${chartId}, skipping chart creation`
          );
          return;
        }

        // Ensure canvas dimensions are set
        canvas.width = 80;
        canvas.height = 32;

        // Create new chart with validated data
        this.monthlyTrendCharts[chartId] = new Chart(ctx, {
          type: "line",
          data: {
            labels: labels.slice(0, data.length), // Ensure labels match data length
            datasets: [
              {
                data,
                borderColor: trendColor,
                backgroundColor: trendColor + "30",
                fill: true,
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 3,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
              },
            },
            scales: {
              x: {
                display: false,
              },
              y: {
                display: false,
                beginAtZero: true,
                min: 0, // Ensure y-axis starts at 0
              },
            },
            animation: false,
            interaction: {
              intersect: false,
            },
          },
        });

        console.log(
          `Chart successfully initialized for keyword: ${keyword.keyword}`
        );
      } catch (error) {
        console.error(`Error creating monthly trend chart ${chartId}:`, error);
        console.error(`Keyword data:`, keyword);
        console.error(`Chart data:`, { data, labels, trendColor });
      }
    },

    generateMockMonthlyData(averageVolume, keyword = "") {
      const months = [];
      const currentDate = new Date();
      const baseVolume = Math.max(100, Number(averageVolume) || 1000); // Ensure minimum volume

      // Create a more unique seed based on keyword and volume
      let seed = baseVolume % 1000;
      if (keyword) {
        // Add keyword characters to seed for more variation
        for (let i = 0; i < keyword.length; i++) {
          seed += keyword.charCodeAt(i) * (i + 1);
        }
      }
      seed = seed % 10000; // Keep seed manageable

      // Determine trend type based on seed
      const trendType = seed % 5; // 0-4 different trend patterns

      for (let i = 11; i >= 0; i--) {
        const date = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() - i,
          1
        );
        const monthName = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });

        let searchVolume;
        const monthProgress = (11 - i) / 11; // 0 to 1 over the 12 months

        switch (trendType) {
          case 0: // Steep declining trend
            {
              const decline = Math.pow(monthProgress, 0.7) * 0.7; // Steeper decline up to 70%
              const randomVar = (((seed + i * 23) % 100) / 100) * 0.1 - 0.05; // ±5% random
              searchVolume = Math.round(baseVolume * (1 - decline + randomVar));
            }
            break;
          case 1: // Strong growing trend
            {
              const growth = Math.pow(monthProgress, 0.8) * 0.8; // Strong growth up to 80%
              const randomVar = (((seed + i * 31) % 100) / 100) * 0.1 - 0.05; // ±5% random
              searchVolume = Math.round(baseVolume * (1 + growth + randomVar));
            }
            break;
          case 2: // U-shaped (dip then recovery)
            {
              const uShape =
                Math.pow(Math.sin(monthProgress * Math.PI), 2) * 0.6 - 0.3; // Dip then rise
              const randomVar = (((seed + i * 41) % 100) / 100) * 0.1 - 0.05; // ±5% random
              searchVolume = Math.round(baseVolume * (1 + uShape + randomVar));
            }
            break;
          case 3: // Highly volatile zigzag
            {
              const zigzag = Math.sin(monthProgress * Math.PI * 4) * 0.4; // Multiple peaks/valleys
              const randomVar = (((seed + i * 37) % 100) / 100) * 0.2 - 0.1; // ±10% random
              searchVolume = Math.round(baseVolume * (1 + zigzag + randomVar));
            }
            break;
          case 4: // Inverted U (peak in middle)
          default:
            {
              const invertedU = Math.sin(monthProgress * Math.PI) * 0.5; // Peak in middle
              const randomVar = (((seed + i * 19) % 100) / 100) * 0.1 - 0.05; // ±5% random
              searchVolume = Math.round(
                baseVolume * (1 + invertedU + randomVar)
              );
            }
            break;
        }

        // Ensure minimum volume
        searchVolume = Math.max(Math.round(baseVolume * 0.1), searchVolume);

        months.push({
          month: monthName,
          search_volume: searchVolume,
        });
      }

      return months;
    },

    showTrendTooltip(event, keyword) {
      if (!keyword || !keyword.keyword) {
        console.warn("Invalid keyword data for tooltip");
        return;
      }

      const rect = event.target.getBoundingClientRect();

      // Use the same data processing logic as the chart
      let monthlyData = [];

      if (
        keyword.monthly_search_data &&
        Array.isArray(keyword.monthly_search_data) &&
        keyword.monthly_search_data.length > 0
      ) {
        const validData = keyword.monthly_search_data.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            item.search_volume !== undefined &&
            item.month !== undefined
        );

        if (validData.length > 0) {
          monthlyData = validData;
        } else {
          monthlyData = this.generateMockMonthlyData(
            keyword.search_volume || 0,
            keyword.keyword
          );
        }
      } else {
        monthlyData = this.generateMockMonthlyData(
          keyword.search_volume || 0,
          keyword.keyword
        );
      }

      let tooltipContent = `<div class="font-medium mb-2">${this.escapeHtml(
        keyword.keyword
      )}</div>`;
      tooltipContent += '<div class="text-sm space-y-1">';

      // Show last 6 months of data
      const recentData = monthlyData.slice(-6);
      recentData.forEach((item) => {
        const month =
          item && typeof item === "object" && item.month
            ? String(item.month)
            : "N/A";
        const volume =
          item && typeof item === "object" ? item.search_volume || 0 : 0;

        tooltipContent += `<div class="flex justify-between">
          <span>${this.escapeHtml(month)}:</span>
          <span class="font-medium">${this.formatSearchVolume(volume)}</span>
        </div>`;
      });

      tooltipContent += "</div>";

      this.trendTooltip = {
        show: true,
        x: rect.left + window.scrollX + rect.width / 2,
        y: rect.top + window.scrollY + rect.height + 10,
        content: tooltipContent,
        keyword: keyword,
      };
    },

    hideTrendTooltip() {
      this.trendTooltip.show = false;
    },


    getTrendTooltipContent(keyword) {
      if (!keyword || !keyword.keyword) {
        return "<div class='text-gray-300'>No trend data available</div>";
      }

      // Use the same data processing logic as the original showTrendTooltip
      let monthlyData = [];

      if (
        keyword.monthly_search_data &&
        Array.isArray(keyword.monthly_search_data) &&
        keyword.monthly_search_data.length > 0
      ) {
        const validData = keyword.monthly_search_data.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            item.search_volume !== undefined &&
            item.month !== undefined
        );

        if (validData.length > 0) {
          monthlyData = validData;
        } else {
          monthlyData = this.generateMockMonthlyData(
            keyword.search_volume || 0,
            keyword.keyword
          );
        }
      } else {
        monthlyData = this.generateMockMonthlyData(
          keyword.search_volume || 0,
          keyword.keyword
        );
      }

      let tooltipContent = "<div class='space-y-1'>";
      
      // Show last 6 months of data
      const recentData = monthlyData.slice(-6);
      recentData.forEach((item) => {
        const month =
          item && typeof item === "object" && item.month
            ? String(item.month)
            : "N/A";
        const volume =
          item && typeof item === "object" ? item.search_volume || 0 : 0;

        tooltipContent += `<div class="flex justify-between text-xs">
          <span class="text-gray-300">${this.escapeHtml(month)}:</span>
          <span class="font-medium text-white">${this.formatSearchVolume(volume)}</span>
        </div>`;
      });

      tooltipContent += "</div>";
      return tooltipContent;
    },

    calculateOpportunityScore(keywordData) {
      // Calculate opportunity score based on search volume, competition, and CPC
      let score = 0;

      // Search volume component (0-40 points)
      const volume = keywordData.search_volume || 0;
      if (volume > 10000) score += 40;
      else if (volume > 5000) score += 30;
      else if (volume > 1000) score += 20;
      else if (volume > 100) score += 10;

      // Competition component (0-30 points) - lower competition = higher score
      const competition = keywordData.competition || "";
      if (competition === "LOW") score += 30;
      else if (competition === "MEDIUM") score += 15;
      else if (competition === "HIGH") score += 5;

      // CPC component (0-30 points) - moderate CPC is good
      const avgCPC =
        ((keywordData.low_top_of_page_bid || 0) +
          (keywordData.high_top_of_page_bid || 0)) /
        2;
      if (avgCPC >= 1 && avgCPC <= 5) score += 30;
      else if (avgCPC >= 0.5 && avgCPC <= 10) score += 20;
      else if (avgCPC > 0) score += 10;

      return Math.min(100, Math.max(0, score));
    },

    async refreshRecommendations() {
      this.isRefreshing = true;
      try {
        await this.loadRecommendations();
        this.$snackbar?.show("Recommendations refreshed");
      } finally {
        this.isRefreshing = false;
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedKeywords = this.filteredRecommendations.map(
          (item) => item.keyword
        );
      } else {
        this.selectedKeywords = [];
      }
    },

    getCompetitionTextColor(competition) {
      const comp = competition || "";
      console.log("compp:", competition);

      if (comp === "LOW") return "text-green-600";
      if (comp === "MEDIUM") return "text-yellow-600";
      if (comp === "HIGH") return "text-red-600";
      return "text-gray-500";
    },

    getCompetitionSortValue(competition) {
      // Convert competition levels to numeric values for sorting
      const comp = competition || "";
      if (comp === "LOW") return 1;
      if (comp === "MEDIUM") return 2;
      if (comp === "HIGH") return 3;
      return 0; // N/A or unknown
    },

    getCompetitionLabel(competition) {
      const comp = competition || "";
      if (comp === "LOW") return "Low";
      if (comp === "MEDIUM") return "Medium";
      if (comp === "HIGH") return "High";
      return "N/A";
    },

    getOpportunityColor(score) {
      if (score >= 80) return "bg-green-500";
      if (score >= 60) return "bg-yellow-500";
      return "bg-red-500";
    },

    formatSearchVolume(volume) {
      if (!volume || volume === 0) return "N/A";
      if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
      if (volume >= 1000) return `${(volume / 1000).toFixed(1)}K`;
      return volume.toString();
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (e) {
        return dateString;
      }
    },

    async addSelectedKeywords() {
      if (this.selectedKeywords.length === 0) return;

      try {
        const result = await this.addKeywordsToTracking(this.selectedKeywords);
        if (result.success) {
          this.$snackbar?.show({
            message: `Added ${result.addedCount} keywords to Rank Tracker`,
            color: "success",
          });
          this.selectedKeywords = [];
        } else {
          this.$snackbar?.show({
            message: result.error,
            color: "error",
          });
        }
      } catch (error) {
        console.error("Error adding selected keywords:", error);
        this.$snackbar?.show({
          message: "Error adding keywords to tracking",
          color: "error",
        });
      }
    },

    async addSingleKeyword(keyword) {
      try {
        const result = await this.addKeywordsToTracking([keyword]);
        if (result.success) {
          this.$snackbar?.show({
            message: `Added "${keyword}" to Rank Tracker`,
            color: "success",
          });
        } else {
          this.$snackbar?.show({
            message: result.error,
            color: "error",
          });
        }
      } catch (error) {
        console.error("Error adding single keyword:", error);
        this.$snackbar?.show({
          message: "Error adding keyword to Rank Tracker",
          color: "error",
        });
      }
    },

    async addKeywordsToTracking(keywordsToAdd) {
      if (!this.currentUID || !this.WID) {
        return {
          success: false,
          error: "Missing user or workspace information",
        };
      }

      try {
        // Get current keywords and document ID
        const { keywords: currentKeywords, docId: currentDocId } =
          await this.getCurrentKeywords();

        // Validate and filter keywords to add
        const validKeywords = keywordsToAdd
          .map((k) => k.trim())
          .filter((k) => k.length >= 2)
          .filter((k) => k.length <= 50);

        if (validKeywords.length === 0) {
          return {
            success: false,
            error: "No valid keywords to add (must be 2-50 characters)",
          };
        }

        // Check for duplicates with existing keywords (case-insensitive)
        const existingKeywordsLower = currentKeywords.map((k) =>
          k.toLowerCase()
        );
        const duplicates = validKeywords.filter((k) =>
          existingKeywordsLower.includes(k.toLowerCase())
        );

        if (duplicates.length > 0) {
          return {
            success: false,
            error: `Keywords already exist: ${duplicates.join(", ")}`,
          };
        }

        // Check total keyword limit (5 max)
        const totalKeywords = currentKeywords.length + validKeywords.length;
        if (totalKeywords > 5) {
          return {
            success: false,
            error: `Maximum 5 keywords allowed.`,
          };
        }

        // Add new keywords to existing ones
        const updatedKeywords = [...currentKeywords, ...validKeywords];

        // Save to Firestore (update latest document if exists, otherwise create next Monday doc)
        const success = await this.saveKeywordsToFirestore(
          updatedKeywords,
          currentDocId
        );

        if (success) {
          return {
            success: true,
            addedCount: validKeywords.length,
          };
        } else {
          return {
            success: false,
            error: "Failed to save keywords to database",
          };
        }
      } catch (error) {
        console.error("Error in addKeywordsToTracking:", error);
        return {
          success: false,
          error: "Error processing keywords",
        };
      }
    },

    async getCurrentKeywords() {
      try {
        // Get the latest keyword document ordered by desc created_at
        const keywordsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "keywords"
        );

        const keywordsQuery = query(
          keywordsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );

        const querySnapshot = await getDocs(keywordsQuery);

        if (querySnapshot.empty) {
          return { keywords: [], docId: null };
        }

        const latestDoc = querySnapshot.docs[0];
        const data = latestDoc.data();

        return {
          keywords: data.keywords || [],
          docId: latestDoc.id,
        };
      } catch (error) {
        console.error("Error fetching current keywords:", error);
        return { keywords: [], docId: null };
      }
    },

    async saveKeywordsToFirestore(keywords, docId = null) {
      try {
        if (docId) {
          // Update existing document
          const keywordDocRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "keywords",
            docId
          );

          await updateDoc(keywordDocRef, {
            keywords: keywords,
            updated_at: new Date(),
          });
          return true;
        } else {
          // Create new document with next Monday's date as ID
          const nextMonday = this.getNextMondayDate();
          const keywordDocRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "keywords",
            nextMonday
          );

          const keywordData = {
            keywords: keywords,
            created_at: new Date(),
          };

          await setDoc(keywordDocRef, keywordData);
          return true;
        }
      } catch (error) {
        console.error("Error saving keywords to Firestore:", error);
        return false;
      }
    },

    getNextMondayDate() {
      const today = moment();
      const nextMonday = today.clone().add(1, "week").startOf("isoWeek");
      return nextMonday.format("YYYY-MM-DD");
    },

    toggleSort(field) {
      if (this.currentSortField === field) {
        // If clicking the same field, toggle direction
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        // If clicking a different field, set it as current and default to ascending
        this.currentSortField = field;
        this.sortDirection = "asc";
      }

      // Map field and direction to sortBy value
      const sortMapping = {
        keyword: { asc: "keyword", desc: "keywordDesc" },
        searchVolume: { asc: "searchVolumeAsc", desc: "searchVolume" },
        competition: { asc: "competition", desc: "competitionDesc" },
        competitionIndex: {
          asc: "competitionIndex",
          desc: "competitionIndexDesc",
        },
        cpc: { asc: "cpc", desc: "cpcDesc" },
        lowBid: { asc: "lowBid", desc: "lowBidDesc" },
        highBid: { asc: "highBid", desc: "highBidDesc" },
      };

      this.sortBy = sortMapping[field][this.sortDirection];
    },

    getSortIcon(field) {
      // Handle case when sortBy is null (use default searchVolume field)
      const activeSortBy = this.sortBy || "searchVolume";
      const activeField = this.getFieldFromSortBy(activeSortBy);

      if (activeField !== field) return "mdi-unfold-more-horizontal";
      return this.getSortDirectionFromSortBy(activeSortBy) === "asc"
        ? "mdi-arrow-up"
        : "mdi-arrow-down";
    },

    getFieldFromSortBy(sortBy) {
      // Extract field name from sortBy value
      if (sortBy.includes("Desc")) {
        return sortBy.replace("Desc", "");
      }
      if (sortBy.includes("Asc")) {
        return sortBy.replace("Asc", "");
      }
      // Handle special cases
      if (sortBy === "searchVolume") return "searchVolume";
      if (sortBy === "keyword") return "keyword";
      return sortBy;
    },

    getSortDirectionFromSortBy(sortBy) {
      // Extract direction from sortBy value
      if (sortBy.includes("Desc")) return "desc";
      if (sortBy.includes("Asc")) return "asc";
      // Default directions for specific sorts
      if (
        sortBy === "searchVolume" ||
        sortBy === "cpcDesc" ||
        sortBy === "lowBidDesc" ||
        sortBy === "highBidDesc" ||
        sortBy === "competitionIndexDesc" ||
        sortBy === "competitionDesc"
      ) {
        return "desc";
      }
      return "asc";
    },

    formatNumber(value) {
      if (value === null || value === undefined) return "N/A";
      return value.toLocaleString();
    },

    escapeHtml(text) {
      if (!text) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },

    getTotalSearchVolume() {
      return this.recommendations.reduce((sum, keyword) => {
        return sum + (keyword.search_volume || 0);
      }, 0);
    },

    getAverageCompetitionIndex() {
      const validIndices = this.recommendations.filter(
        (k) => k.competition_index && k.competition_index > 0
      );
      if (validIndices.length === 0) return "N/A";

      const totalIndex = validIndices.reduce((sum, keyword) => {
        return sum + (keyword.competition_index || 0);
      }, 0);
      return Math.round(totalIndex / validIndices.length);
    },

    getLowestCPC() {
      const validCPCs = this.recommendations.filter((k) => k.cpc && k.cpc > 0);
      if (validCPCs.length === 0) return 0;

      const lowestCPC = Math.min(...validCPCs.map((k) => k.cpc));
      return lowestCPC;
    },

    getHighestCPC() {
      const validCPCs = this.recommendations.filter((k) => k.cpc && k.cpc > 0);
      if (validCPCs.length === 0) return 0;

      const highestCPC = Math.max(...validCPCs.map((k) => k.cpc));
      return highestCPC;
    },

    getLowestLowTopBid() {
      const validBids = this.recommendations.filter(
        (k) => k.low_top_of_page_bid && k.low_top_of_page_bid > 0
      );
      if (validBids.length === 0) return 0;

      const lowestLowBid = Math.min(
        ...validBids.map((k) => k.low_top_of_page_bid)
      );
      return lowestLowBid;
    },

    getHighestLowTopBid() {
      const validBids = this.recommendations.filter(
        (k) => k.low_top_of_page_bid && k.low_top_of_page_bid > 0
      );
      if (validBids.length === 0) return 0;

      const highestLowBid = Math.max(
        ...validBids.map((k) => k.low_top_of_page_bid)
      );
      return highestLowBid;
    },

    getLowestHighTopBid() {
      const validBids = this.recommendations.filter(
        (k) => k.high_top_of_page_bid && k.high_top_of_page_bid > 0
      );
      if (validBids.length === 0) return 0;

      const lowestHighBid = Math.min(
        ...validBids.map((k) => k.high_top_of_page_bid)
      );
      return lowestHighBid;
    },

    getHighestHighTopBid() {
      const validBids = this.recommendations.filter(
        (k) => k.high_top_of_page_bid && k.high_top_of_page_bid > 0
      );
      if (validBids.length === 0) return 0;

      const highestHighBid = Math.max(
        ...validBids.map((k) => k.high_top_of_page_bid)
      );
      return highestHighBid;
    },

    getCompetitionDistribution() {
      const low = this.recommendations.filter(
        (keyword) => keyword.competition === "LOW"
      ).length;
      const medium = this.recommendations.filter(
        (keyword) => keyword.competition === "MEDIUM"
      ).length;
      const high = this.recommendations.filter(
        (keyword) => keyword.competition === "HIGH"
      ).length;
      return { low, medium, high };
    },

    getCompetitionPercentage(level) {
      const total = this.recommendations.length;
      if (total === 0) return 0;
      const count = this.getCompetitionDistribution()[level];
      return ((count / total) * 100).toFixed(1);
    },

    async formatCurrencyValue(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) {
        return "N/A";
      }

      // Create cache key
      const cacheKey = `${amount}-${this.userCountryCode}`;

      // Check cache first
      if (this.currencyConversionCache.has(cacheKey)) {
        return this.currencyConversionCache.get(cacheKey);
      }

      try {
        // Convert and format currency
        const formattedValue = await formatCurrencyWithConversion(
          amount,
          this.userCountryCode
        );

        // Cache the result
        this.currencyConversionCache.set(cacheKey, formattedValue);

        return formattedValue;
      } catch (error) {
        console.error("Currency conversion error:", error);
        // Fallback to simple formatting without conversion
        return formatCurrency(amount, this.userCountryCode);
      }
    },

    // Convert overview prices (called when data changes)
    async convertOverviewPrices() {
      if (this.recommendations.length === 0) return;

      this.isConvertingCurrency = true;

      try {
        const [
          lowestCPC,
          highestCPC,
          lowestLowTopBid,
          highestLowTopBid,
          lowestHighTopBid,
          highestHighTopBid,
        ] = await Promise.all([
          this.formatCurrencyValue(this.getLowestCPC()),
          this.formatCurrencyValue(this.getHighestCPC()),
          this.formatCurrencyValue(this.getLowestLowTopBid()),
          this.formatCurrencyValue(this.getHighestLowTopBid()),
          this.formatCurrencyValue(this.getLowestHighTopBid()),
          this.formatCurrencyValue(this.getHighestHighTopBid()),
        ]);

        this.convertedPrices = {
          lowestCPC,
          highestCPC,
          lowestLowTopBid,
          highestLowTopBid,
          lowestHighTopBid,
          highestHighTopBid,
        };
      } catch (error) {
        console.error("Error converting overview prices:", error);
      } finally {
        this.isConvertingCurrency = false;
      }
    },

    // Get loading text based on user's currency
    getLoadingText() {
      return this.userCountryCode === "US" ? "$..." : "...";
    },
  },
};
</script>

<style scoped>
.recommended-keywords-container {
  min-height: 100vh;
  background-color: #f8fafc;
}

/* Custom checkbox styling for better visibility */
.v-input--checkbox .v-input__slot {
  margin-bottom: 0;
}

/* Table styling improvements */
table {
  border-collapse: separate;
  border-spacing: 0;
}

th:first-child,
td:first-child {
  border-left: 1px solid #e5e7eb;
}

th:last-child,
td:last-child {
  border-right: 1px solid #e5e7eb;
}

th {
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

td {
  border-bottom: 1px solid #e5e7eb;
}

/* Hover effects */
tbody tr:hover {
  background-color: #f9fafb;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .recommended-keywords-container {
    padding: 1rem;
  }

  .flex-wrap {
    flex-direction: column;
  }

  .min-w-64,
  .min-w-48 {
    min-width: 100%;
  }
}
</style>
